/**
 * @lodex-pro/shared — operator public API idempotency keys (Phase 18 / Task 18.6,
 * migration 0116).
 *
 * One row per `Idempotency-Key` a credential has used on `POST /haul-requests`, naming the
 * job that key created. The row is inserted in the SAME transaction as the job, so a
 * concurrent retry with the same key blocks on the unique index until the first commits,
 * then replays it — there is no window in which two requests both create a job.
 *
 * A separate module rather than a table in `operator-api-credentials.ts`: this table
 * references `jobs`, and `jobs` references the credentials table, so housing it there would
 * make the two schema files import each other.
 *
 * RLS-scoped on the ISSUING HAULER (DEC-239). The SQL also carries a COMPOSITE foreign key
 * (tenant_id, credential_id) → operator_api_credentials (tenant_id, id), the 0112
 * convention; drizzle declares the columns, the migration owns the constraint.
 *
 * No expiry in v1 — see the 0116 header for why a 24-hour window would double-book a truck.
 */

import { char, check, pgPolicy, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { appUserRole } from './enums.js';
import { tenants } from './users.js';
import { jobs } from './jobs.js';

export const operatorApiIdempotencyKeys = pgTable(
  'operator_api_idempotency_keys',
  {
    id: uuid('id')
      .primaryKey()
      .default(sql`gen_random_uuid()`),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    credentialId: uuid('credential_id').notNull(),
    idempotencyKey: text('idempotency_key').notNull(),
    /** SHA-256 hex of the canonicalised request body. */
    requestHash: char('request_hash', { length: 64 }).notNull(),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('operator_api_idempotency_keys_credential_key_key').on(t.credentialId, t.idempotencyKey),
    check(
      'operator_api_idempotency_keys_key_length_check',
      sql`char_length(idempotency_key) BETWEEN 1 AND 255`,
    ),
    pgPolicy('operator_api_idempotency_keys_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
      withCheck: sql`tenant_id::text = current_setting('app.current_tenant_id', true)`,
    }),
  ],
);

export type OperatorApiIdempotencyKey = typeof operatorApiIdempotencyKeys.$inferSelect;
